/**
 * Confirm dialog for publishing a bank (cascade) or the "Publish all items"
 * repair action. Lists the unpublished questions, all ticked; editors untick
 * any that should stay out.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

const STATUS_LABEL = { draft: 'Draft', in_review: 'In review', approved: 'Approved' };

export function PublishBankDialog({ open, onOpenChange, items, mode, onConfirm }) {
  const [excluded, setExcluded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setExcluded(new Set()); }, [open]);

  const selected = items.length - excluded.size;
  const toggle = (id) => setExcluded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setAll = (on) => setExcluded(on ? new Set() : new Set(items.map(i => i.item_id)));

  const confirm = async () => {
    setBusy(true);
    try { await onConfirm([...excluded]); onOpenChange(false); } finally { setBusy(false); }
  };

  const isBank = mode === 'bank';
  const title = isBank
    ? `Publish bank and its ${selected} question${selected === 1 ? '' : 's'}?`
    : `Publish ${selected} question${selected === 1 ? '' : 's'}?`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="publish-bank-dialog">
        <DialogHeader>
          <DialogTitle data-testid="publish-bank-title">{title}</DialogTitle>
          <DialogDescription>
            {isBank ? 'This publishes the bank and the ticked questions. ' : 'The ticked questions become published. '}
            Untick any that should stay as they are. Retired questions are not affected.
          </DialogDescription>
        </DialogHeader>
        {items.length > 0 && (
          <>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="publish-bank-count">{selected} of {items.length} selected</span>
              <span className="flex gap-3">
                <button type="button" className="hover:underline" onClick={() => setAll(true)} data-testid="publish-bank-select-all">Select all</button>
                <button type="button" className="hover:underline" onClick={() => setAll(false)} data-testid="publish-bank-select-none">None</button>
              </span>
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
              {items.map(it => (
                <label key={it.item_id} className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                       data-testid={`publish-bank-item-${it.item_id}`}>
                  <Checkbox checked={!excluded.has(it.item_id)} onCheckedChange={() => toggle(it.item_id)}
                            className="mt-0.5" data-testid={`publish-bank-check-${it.item_id}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm truncate">{it.question_text}</span>
                    <span className="text-[11px] text-muted-foreground">{STATUS_LABEL[it.status] || it.status} · v{it.version}</span>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={confirm} disabled={busy || (!isBank && selected === 0)} data-testid="publish-bank-confirm-btn">
            {busy ? 'Publishing…' : isBank ? 'Publish bank' : `Publish ${selected}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
